import { useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { useFillChartFrame } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/chart-frame'
import { drawChart } from '@aytm/app/stats/page/question-views/vk/components/chart/lib/chart'
import { drawWestenDorpChart } from '@aytm/app/stats/page/question-views/vk/components/chart/lib/chart-westendorp'
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { useRenderGate } from '@aytm/app/stats/page/question-views/components/charts/placeholders/lib/render-gate'

// components
import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

// styles
import style from '@aytm/app/stats/page/question-views/vk/components/chart/styles/Chart.module.scss'

const DEFAULT_WIDTH = 746

const exportOptions = [
  {
    text: 'Download PNG image',
    key: 'png'
  },
  {
    text: 'Download JPEG image',
    key: 'jpeg'
  },
  {
    text: 'Download PDF document',
    key: 'pdf'
  }
]

// pptx.coffee walks these suffixes in order and expects one chart per entry; the position
// matches captureIndex, which already fixes the headless-export ordering.
export const VK_EXPORT_SUFFIXES = ['', '_wd', '_pp', '_pf', '_pr']

export const vkExportId = (id, captureIndex = 0) =>
  id == null ? undefined : `chart_question_${id}${VK_EXPORT_SUFFIXES[captureIndex] ?? ''}`

const VanKonanChart = ({ id, model, palette, infographic, isWestendorp, onExport, captureIndex }) => {
  const svgRef = useRef(null)

  // what the placeholder above stood in for is this chart, so it is this chart that answers for it
  useFillChartFrame(svgRef)
  const { componentRef, width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })
  const chartRendered = useRenderGate()

  const onExportClick = useCallback(
    (fileType, callback) => {
      const data = (null !== svgRef.current ? svgRef.current.cloneNode(true) : void 0) || null

      onExport(
        {
          fileType
        },
        data,
        callback
      )
    },
    [onExport]
  )

  useEffect(() => {
    if (svgRef.current) {
      const options = {
        model,
        width,
        height: 426,
        axisWidth: width - 12,
        axisHeight: !infographic ? 376 : 356,
        infographic,
        palette
      }

      isWestendorp ? drawWestenDorpChart(svgRef.current, options) : drawChart(svgRef.current, options)
    }

    chartRendered()
  }, [model, width, isWestendorp, infographic, palette, chartRendered])

  return (
    <div
      ref={componentRef}
      className={classNames(style.chart, {
        [style.chartWestendorp]: isWestendorp,
        [style.chartInverse]: isWestendorp || infographic
      })}
    >
      <div className={style.export}>
        <ExportDropdown
          className={classNames({
            [style.exportInfographic]: infographic
          })}
          options={exportOptions}
          onExport={onExportClick}
        />
      </div>
      {/* data-capture-index marks this svg as a headless-export target and fixes
          its order in the multi-output array. Omitted (undefined) on the live page. */}
      {/* pptx.coffee looks the chart up as `#chart_question_<id><suffix> svg`, so the id has to
          sit on a wrapper around the svg rather than on the svg itself. */}
      <div id={vkExportId(id, captureIndex)}>
        <svg ref={svgRef} data-capture-index={captureIndex} />
      </div>
    </div>
  )
}

VanKonanChart.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  palette: PropTypes.array,
  chartMode: PropTypes.string,
  model: PropTypes.object,
  infographic: PropTypes.bool,
  isWestendorp: PropTypes.bool,
  onExport: PropTypes.func,
  captureIndex: PropTypes.number
}

export default VanKonanChart
